import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { useEffect } from 'react'
import { useLocation } from '@tanstack/react-router'
import type { WorkspaceKey } from '@/components/WorkspaceSwitcher'

/**
 * Tiny persisted store that remembers the last URL the user
 * visited within each top-level workspace (bussboard / werkshop).
 * The WorkspaceSwitcher reads from here so clicking a pill jumps
 * to the user's last editing context rather than the workspace
 * root — e.g. clicking Bussboard takes you back to the environment
 * you were editing, not the list.
 *
 * Paths are stored as-is (including params). The switcher
 * validates them against the running TanStack Router route tree
 * before navigating, so stale deep-links can't break the pill.
 */

export type NavWorkspace = WorkspaceKey

/** Workspace → URL-prefix matchers. First match wins. */
const WORKSPACE_PREFIXES: Array<{ key: NavWorkspace; test: RegExp }> = [
  { key: 'bussboard', test: /^\/bussboard(?:\/|$)/ },
  { key: 'werkshop', test: /^\/werkshop(?:\/|$)/ },
]

export function resolveWorkspace(pathname: string): NavWorkspace | null {
  for (const entry of WORKSPACE_PREFIXES) {
    if (entry.test.test(pathname)) return entry.key
  }
  return null
}

interface NavState {
  /** Full pathname (with params) of the most recent visit per
   *  workspace. Empty string = never visited; callers should
   *  fall back to the category's root route. */
  lastSeen: Record<NavWorkspace, string>
  recordVisit: (pathname: string) => void
}

const DEFAULT_LAST_SEEN: Record<NavWorkspace, string> = {
  bussboard: '',
  werkshop: '',
}

export const useNavStore = create<NavState>()(
  persist(
    set => ({
      lastSeen: DEFAULT_LAST_SEEN,
      recordVisit: pathname => {
        const workspace = resolveWorkspace(pathname)
        if (!workspace) return
        set(state => {
          if (state.lastSeen[workspace] === pathname) return state
          return {
            lastSeen: { ...state.lastSeen, [workspace]: pathname },
          }
        })
      },
    }),
    {
      name: 'busswerks-nav',
      storage: createJSONStorage(() =>
        typeof window === 'undefined'
          ? {
              getItem: () => null,
              setItem: () => {},
              removeItem: () => {},
            }
          : window.localStorage,
      ),
    },
  ),
)

/** Mounted once at the app root. Watches the router location and
 *  pushes workspace-relevant visits into the nav store. */
export function useNavigationTracker(): void {
  const recordVisit = useNavStore(s => s.recordVisit)
  const location = useLocation()
  useEffect(() => {
    recordVisit(location.pathname)
  }, [location.pathname, recordVisit])
}

/** Resolve the effective target URL for a workspace pill. Returns
 *  the last-seen path when present; otherwise the workspace's
 *  root route. */
export function resolveWorkspaceHref(
  workspace: NavWorkspace,
  fallback: string,
): string {
  const last = useNavStore.getState().lastSeen[workspace]
  return last && last.length > 0 ? last : fallback
}
